import { useState, useEffect, useMemo, useId } from 'react';
import { motion } from 'framer-motion';
import { fmtTime, fmtDate, heightAt, type DayTides, type TidePoint } from './tide-panels';

export default function TideChart({ days }: { days: DayTides[] }) {
  // Unique gradient IDs per instance — this component is rendered twice (mobile + desktop),
  // and duplicate SVG ids + display:none broke url(#...) paint references, hiding the curve.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const areaId = `tideArea-${uid}`;
  const lineId = `tideLine-${uid}`;
  const [dayIdx, setDayIdx] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(timer);
  }, []);

  // Reset to "Today" when the data set changes (port switch)
  useEffect(() => { setDayIdx(0); }, [days]);

  const allEvents = useMemo<TidePoint[]>(() => {
    const evs: TidePoint[] = [];
    for (const day of days) {
      for (const t of day.tides) {
        evs.push({ t: new Date(t.time).getTime(), h: t.height, type: t.type });
      }
    }
    return evs.sort((a, b) => a.t - b.t);
  }, [days]);

  const selectedDay = days[Math.min(dayIdx, days.length - 1)];
  if (!selectedDay) return null;

  const dayStart = new Date(selectedDay.date);
  dayStart.setHours(0, 0, 0, 0);
  const winStart = dayStart.getTime();
  const winEnd = winStart + 24 * 3600 * 1000;

  const samples = useMemo(() => {
    const pts: { x: number; h: number }[] = [];
    const step = 5 * 60 * 1000;
    for (let t = winStart; t <= winEnd; t += step) {
      pts.push({ x: t, h: heightAt(allEvents, t) });
    }
    return pts;
  }, [winStart, winEnd, allEvents]);

  const heights = samples.map(p => p.h);
  let minH = Math.min(...heights);
  let maxH = Math.max(...heights);
  const pad = Math.max(0.15, (maxH - minH) * 0.15);
  minH = Math.max(0, minH - pad);
  maxH = maxH + pad;

  const W = 1000, H = 300;
  const PL = 46, PR = 16, PT = 34, PB = 34;
  const iw = W - PL - PR;
  const ih = H - PT - PB;
  const xOf = (t: number) => PL + ((t - winStart) / (winEnd - winStart)) * iw;
  const yOf = (h: number) => PT + (1 - (h - minH) / (maxH - minH)) * ih;

  const linePath = samples.map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(p.x).toFixed(1)},${yOf(p.h).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${xOf(winEnd).toFixed(1)},${(PT + ih).toFixed(1)} L${xOf(winStart).toFixed(1)},${(PT + ih).toFixed(1)} Z`;

  const markers = allEvents.filter(e => e.t >= winStart && e.t <= winEnd);
  const hourTicks: number[] = [];
  for (let hh = 0; hh <= 24; hh += 3) hourTicks.push(winStart + hh * 3600 * 1000);
  const yTicks: number[] = [];
  for (let i = 0; i <= 3; i++) yTicks.push(minH + ((maxH - minH) * i) / 3);

  const isToday = new Date().toDateString() === dayStart.toDateString();
  const nowMs = now.getTime();
  const showNow = isToday && nowMs >= winStart && nowMs <= winEnd;
  const nowH = showNow ? heightAt(allEvents, nowMs) : 0;
  const nowX = showNow ? xOf(nowMs) : 0;
  const nowY = showNow ? yOf(nowH) : 0;
  const rising = showNow ? heightAt(allEvents, nowMs + 10 * 60000) > nowH : null;

  return (
    <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] bg-card-light dark:bg-card overflow-hidden mb-4">
      <div className="px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-display text-sm font-bold">Tide Curve</div>
          <div className="text-text-muted text-[0.7rem]">
            {dayIdx === 0 ? 'Today' : selectedDay.dayName} · {fmtDate(selectedDay.date)}
            {showNow && (
              <span className="ml-2 font-semibold text-accent">
                {rising ? '▲ Rising' : '▼ Falling'} · {nowH.toFixed(1)}m now
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {days.map((d, i) => (
            <button
              key={i}
              onClick={() => setDayIdx(i)}
              className={`px-2.5 py-1 rounded-lg text-[0.65rem] font-bold transition-all ${
                i === dayIdx
                  ? 'bg-accent text-white dark:text-bg'
                  : 'bg-surface-light dark:bg-surface text-text-secondary border border-[rgba(16,42,66,0.09)] hover:border-accent/40'
              }`}
            >
              {i === 0 ? 'Today' : d.dayName.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>

      <div className="px-2 pb-2">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none" role="img" aria-label="Tide height curve">
          <defs>
            <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(79,179,217,0.45)" />
              <stop offset="70%" stopColor="rgba(79,179,217,0.08)" />
              <stop offset="100%" stopColor="rgba(79,179,217,0.02)" />
            </linearGradient>
            <linearGradient id={lineId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#4fb3d9" />
              <stop offset="100%" stopColor="#6fd3f2" />
            </linearGradient>
          </defs>

          {yTicks.map((h, i) => (
            <g key={i}>
              <line x1={PL} x2={W - PR} y1={yOf(h)} y2={yOf(h)} stroke="rgba(127,155,181,0.15)" strokeDasharray="3 5" />
              <text x={PL - 8} y={yOf(h) + 4.5} textAnchor="end" fontSize="14" fill="currentColor" className="text-text-muted" opacity="0.85">
                {h.toFixed(1)}
              </text>
            </g>
          ))}

          {hourTicks.map((t, i) => (
            <text
              key={i}
              x={Math.min(Math.max(xOf(t), PL + 20), W - PR - 20)}
              y={H - 8}
              textAnchor="middle"
              fontSize="16"
              fontWeight="600"
              fill="currentColor"
              className="text-text-secondary"
              opacity="0.9"
            >
              {new Date(t).toLocaleTimeString('en-NZ', { hour: 'numeric', hour12: true }).replace(' ', '')}
            </text>
          ))}

          <motion.path
            key={`area-${dayIdx}-${selectedDay.date}`}
            d={areaPath}
            fill={`url(#${areaId})`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
          <motion.path
            key={`line-${dayIdx}-${selectedDay.date}`}
            d={linePath}
            fill="none"
            stroke={`url(#${lineId})`}
            strokeWidth="3"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />

          {markers.map((m, i) => {
            const cx = xOf(m.t);
            const cy = yOf(m.h);
            const isHigh = m.type === 'high';
            return (
              <g key={i}>
                <circle cx={cx} cy={cy} r="5.5" fill={isHigh ? '#4fb3d9' : 'rgba(16,42,66,0.85)'} stroke={isHigh ? '#bfe9f8' : '#4fb3d9'} strokeWidth="2" />
                <text x={cx} y={isHigh ? cy - 14 : cy + 26} textAnchor="middle" fontSize="15" fontWeight="700" fill={isHigh ? '#4fb3d9' : 'currentColor'} className={isHigh ? '' : 'text-text-secondary'}>
                  {isHigh ? 'H' : 'L'} {fmtTime(new Date(m.t).toISOString())}
                </text>
                <text x={cx} y={isHigh ? cy - 32 : cy + 42} textAnchor="middle" fontSize="13" fill="currentColor" className="text-text-muted">
                  {m.h.toFixed(1)}m
                </text>
              </g>
            );
          })}

          {showNow && (
            <g>
              <line x1={nowX} x2={nowX} y1={PT - 10} y2={PT + ih} stroke="#ef6464" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.8" />
              <circle cx={nowX} cy={nowY} r="6" fill="#ef6464" opacity="0.25">
                <animate attributeName="r" values="5;9;5" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={nowX} cy={nowY} r="3.5" fill="#ef6464" stroke="#fff" strokeWidth="1.5" />
              <text x={Math.min(nowX + 10, W - PR - 42)} y={PT - 14} fontSize="14" fontWeight="700" fill="#ef6464">NOW</text>
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}
